import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { monthLabel } from "@/lib/format";
import { toast } from "sonner";
import { PlayCircle, Trash2 } from "lucide-react";
import { FIELD } from "./imageUtils";

const STEP = { upload: "Waiting for photos", scanning: "AI reading pages", needs_attention: "Some pages failed", review: "Ready for review", approved: "Approved" };
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "");

export const SessionsList = () => {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const load = () => api.get("/verification/sessions").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const start = async () => {
    try {
      const { data } = await api.post("/verification/sessions", { month });
      if (data.existing) toast.info("An unfinished verification for this month already exists — opening it.");
      navigate(`/admin/verification/${data.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const discard = async (s) => {
    if (!window.confirm(`Discard the unfinished verification for ${monthLabel(s.month)}? Photos and read lines will be removed. Website sales are not affected.`)) return;
    try { await api.delete(`/verification/sessions/${s.id}`); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border rounded-xl p-4 flex flex-wrap items-end gap-3">
        <label className="block"><span className="text-xs font-medium text-slate-600">Month</span>
          <Input type="month" data-testid="verification-month-input" className={`${FIELD} w-48`} value={month} onChange={(e) => setMonth(e.target.value)} /></label>
        <Button type="button" data-testid="start-session-button" className="h-11 bg-sky-600 hover:bg-sky-700" onClick={start}>Start / Continue Verification</Button>
      </div>
      <div data-testid="sessions-list" className="bg-white border rounded-xl divide-y">
        {list && list.length === 0 && <p className="p-4 text-sm text-slate-400">No verifications yet.</p>}
        {(list || []).map((s) => (
          <div key={s.id} data-testid={`session-${s.id}`} className="p-4 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px]">
              <div className="font-semibold">{monthLabel(s.month)} {s.plants.length ? `· ${s.plants.join(" & ")}` : ""}</div>
              <div className="text-xs text-slate-500">
                {s.page_total} page(s) · {s.pages.done + s.pages.manual} done{s.pages.processing + s.pages.pending ? ` · ${s.pages.processing + s.pages.pending} in queue` : ""}{s.pages.failed ? ` · ${s.pages.failed} failed` : ""}
                {s.step !== "approved" && s.open_questions ? ` · ${s.open_questions} question(s)` : ""}
              </div>
              <div className="text-xs text-slate-500">Status: <b data-testid={`session-step-${s.id}`}>{STEP[s.step]}</b> · Last updated {when(s.updated_at)}</div>
            </div>
            <Button type="button" className="h-11" data-testid={`continue-session-${s.id}`} onClick={() => navigate(`/admin/verification/${s.id}`)}>
              <PlayCircle className="h-4 w-4 mr-1" />{s.step === "approved" ? "View" : "Continue Verification"}
            </Button>
            {s.step !== "approved" && <Button type="button" variant="ghost" className="h-11 text-red-600" data-testid={`discard-session-${s.id}`} onClick={() => discard(s)}><Trash2 className="h-4 w-4" /></Button>}
          </div>
        ))}
      </div>
    </div>
  );
};
